<template>
    <div class="relative">
        <div
            class="group flex w-full min-w-0 items-center gap-3 rounded-full border border-transparent bg-dark px-4 py-2 text-primary-lighter shadow-lg transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-300 hover:border-primary-medium hover:bg-primary-dark"
            :class="containerClass"
        >
            <label v-if="label" :for="id" class="shrink-0 text-lg font-medium whitespace-nowrap text-primary-light italic">
                {{ label }} <span v-if="required" class="text-red-600">*</span> :
            </label>
            <input
                :id="id"
                :value="modelValue"
                @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
                @blur="onBlur"
                :type="type"
                :placeholder="placeholder"
                :disabled="disabled"
                :min="min"
                :aria-describedby="describedByIds || undefined"
                :aria-invalid="!!error"
                :aria-label="label"
                :class="inputClass"
            />
        </div>
        <p v-if="error" :id="`${id}-error`" class="mt-1 text-sm text-red-600">{{ error }}</p>
        <p v-if="hint" :id="`${id}-hint`" class="mt-1 text-xs text-gray-500">{{ hint }}</p>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

interface Props {
    modelValue: string | number;
    label?: string;
    type?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    error?: string;
    showError?: boolean;
    min?: string;
    hint?: string;
    baseClass?: string;
    validate?: (value: string) => boolean;
    validateMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
    type: 'text',
    baseClass:
        'w-full min-w-0 rounded-xl bg-primary-light p-1 px-3 text-lg font-bold tracking-wide text-dark placeholder:text-dark/60 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
    (e: 'validate', isValid: boolean): void;
    (e: 'blur'): void;
}>();

const id = ref(`input-${Math.random().toString(36).substr(2, 9)}`);

const containerClass = computed(() => {
    if (!(props.showError && props.error)) {
        return '';
    }

    return 'border-red-500 hover:border-red-500 focus-within:ring-red-400';
});

const inputClass = computed(() => {
    const hasError = props.showError && props.error;
    return hasError ? `${props.baseClass} bg-red-50 text-red-700 placeholder:text-red-400` : props.baseClass;
});

const describedByIds = computed(() => {
    const ids: string[] = [];
    if (props.error) ids.push(`${id.value}-error`);
    if (props.hint) ids.push(`${id.value}-hint`);
    return ids.join(' ');
});

const validateField = () => {
    if (props.validate && props.validateMessage) {
        const isValid = props.validate(String(props.modelValue));
        emit('validate', isValid);
    }
};

const onBlur = () => {
    validateField();
    emit('blur');
};
</script>
